<template>
  <section class="w-100 container mt-5 mb-5" :class="{ alternative: alternativeLayout }">
    <div v-if="isDefaultFaq">
      <h2 v-if="getExperience" class="title-faq">
        {{ $t(`${plansPage}.${getExperience}.faq.title`) }}
      </h2>
      <h2 v-else class="title-faq">
        {{ title.length ? title[0] : $t('home.newVexels.faqs.defaultTitle') }}
      </h2>
    </div>
    <prismic-rich-text
      v-else
      :field="title"
      class="title-faq text-center w-100"
    ></prismic-rich-text>
    <prismic-rich-text
      v-if="alternativeLayout && prismicProps.subtitle"
      :field="prismicProps.subtitle"
      class="subtitle-faq text-center w-100"
    ></prismic-rich-text>
    <div class="row">
      <div :class="{ 'col-12 col-md-6 two-cols-right': twoColumns, 'w-100': alternativeLayout }">
        <div
          v-for="(item, index) in firstColumnItems"
          :key="index"
          class="faq-item"
          :style="{ borderBottom: borderBottom }"
        >
          <FaqItem
            :item="item"
            v-bind="commonItemProps"
            :item-id="index"
            :alternative-layout="alternativeLayout"
            @toggleFaq="toggleFaq($event)"
          />
        </div>
      </div>
      <div v-if="twoColumns" class="col-12 col-md-6 two-cols-left">
        <div v-for="(item, index) in secondColumnItems" :key="index" class="faq-item">
          <FaqItem
            :item="item"
            v-bind="commonItemProps"
            :alternative-layout="alternativeLayout"
            :item-id="index + firstColumnItems.length"
            @toggleFaq="toggleFaq($event)"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import FaqItem from '@/components/Common/Faq/FaqItem';

export default {
  name: 'Faq',
  components: {
    FaqItem
  },
  props: {
    newVexels: {
      type: Array,
      required: false,
      default: () => []
    },
    customFaq: {
      type: Array,
      required: false,
      default: () => []
    },
    title: {
      type: Array,
      required: false,
      default: () => []
    },
    prismicProps: {
      type: Object,
      required: false,
      default: () => ({})
    },
    lifetime: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data() {
    return {
      items: []
    };
  },
  jsonld() {
    if (!this.items.length) {
      return null;
    }

    const items = this.items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: this.isDefaultFaq ? item.answer : item.answer[0]?.text
      }
    }));

    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items
    };
  },
  computed: {
    ...mapGetters('experience', ['getExperience']),
    plansPage() {
      return this.lifetime ? 'lifetimePlans' : 'plans';
    },
    isDefaultFaq() {
      return this.customFaq.length === 0;
    },
    textColor() {
      return this.alternativeLayout ? '#2e2e2e' : this.prismicProps?.text_color || '#707070';
    },
    textDecoration() {
      return !this.prismicProps || this.prismicProps?.underline
        ? `text-decoration: underline ${this.textColor}`
        : 'none';
    },
    borderBottom() {
      return this.prismicProps?.border_bottom ? `1px solid ${this.textColor}` : 'none';
    },
    answerStyle() {
      return {
        color: this.textColor,
        marginLeft: !this.isDefaultFaq && this.prismicProps?.itemImage ? '86px !important' : '0px'
      };
    },
    twoColumns() {
      return this.prismicProps?.twoColumns;
    },
    firstColumnItems() {
      const half = Math.ceil(this.items.length / 2);
      return this.twoColumns ? this.items.slice(0, half) : this.items;
    },
    secondColumnItems() {
      return this.items.slice(this.firstColumnItems.length, this.items.length);
    },
    commonItemProps() {
      return {
        textColor: this.textColor,
        textDecoration: this.textDecoration,
        isDefaultFaq: this.isDefaultFaq,
        answerStyle: this.answerStyle
      };
    },
    alternativeLayout() {
      return this.prismicProps?.alternativeLayout;
    }
  },
  created() {
    this.loadFaq();
  },
  methods: {
    loadFaq() {
      this.items = this.isDefaultFaq ? this.loadDefautlFaq() : this.loadCustomFaq();
    },
    loadCustomFaq() {
      return this.customFaq.map((el) => {
        return {
          question: el.question,
          answer: el.answer,
          image: this.prismicProps?.itemImage ? this.prismicProps.itemImage : '',
          isOpen: false
        };
      });
    },
    loadDefautlFaq() {
      if (this.newVexels.length) {
        return this.newVexels;
      } else {
        return [1, 2, 3, 4, 5, 6, 7, 8].map((n) => {
          return {
            question: this.$t(`${this.plansPage}.${this.getExperience}.faq.question${n}`),
            answer: this.$t(`${this.plansPage}.${this.getExperience}.faq.answer${n}`),
            isOpen: false
          };
        });
      }
    },
    toggleFaq(idx) {
      this.items[idx].isOpen = !this.items[idx].isOpen;
    }
  }
};
</script>

<style lang="scss" scoped>
.title-faq {
  color: #787878;
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 25px;
}
.two-cols-right {
  padding: 0 20px 0 10px;
  @include md {
    padding: 0;
  }
}
.two-cols-left {
  padding: 0 10px 0 20px;
  @include md {
    padding: 0;
  }
}
.faq-item {
  margin-bottom: 25px;
}

.alternative {
  .faq-item {
    box-shadow: 0px 3px 38px #00000015;
    border-radius: 24px;
  }
}
.alternative::v-deep {
  .title-faq {
    margin-bottom: 15px;
    > * {
      color: $lightBlue;
      font-size: 15px;
    }
  }
  .subtitle-faq {
    font-size: 33px;
    font-weight: 500;
    margin-bottom: 2rem;
    @include sm {
      font-size: 27px;
      margin-bottom: 1rem;
    }
  }
}
</style>
